import { useRef } from "react";

import { jjLogger } from "utils/logUtils";
import { trackEventVideoRenderedError } from "utils/gAnalyticsUtils";

const useWebGlPlayer = (params) => {
  const transportRef = useRef(null);
  const playerRef = useRef(null);

  const init = (onLoaded = null, onFailed = null, onWebGlError = null) => {
    import("webgl-render-library")
      .then((renderLib) => {
        const { SYPlayer, Transport } = renderLib;

        jjLogger.logDebug("useWebGlPlayer.js: initializing SYPlayer");
        try {
          playerRef.current = new SYPlayer(params);
          playerRef.current.addEventListener("loaded", (event) => {
            jjLogger.logDebug("useWebGlPlayer.js: SYPlayer 'loaded' triggered, initializing Transport");
            transportRef.current = new Transport(event.currentTarget);
            if (typeof onLoaded === "function") onLoaded(event);
          });
        } catch (error) {
          if (typeof onWebGlError === "function") onWebGlError(error);
          jjLogger.logError(`WebGL Player Error: ${error.message}`);
          trackEventVideoRenderedError("WebGL");
        }
      })
      .catch((error) => {
        if (typeof onFailed === "function") onFailed(error);
        jjLogger.logError(`WebGL Player Error: ${error.message}`);
        trackEventVideoRenderedError("WebGL");
      });
  };

  const cleanup = () => {
    if (!playerRef.current) return;

    playerRef.current.removeEventListener("loaded", null);
    playerRef.current.destroy();

    if (transportRef.current) {
      transportRef.current.destroy();
    }

    playerRef.current = null;
    transportRef.current = null;
  };

  const exitFullscreen = () => transportRef.current.closeFullScreen();

  const pausePlayer = () => {
    if (playerRef?.current) {
      playerRef.current.pause();
    }
  };

  const resetPlayer = () => {
    playerRef.current.seekTo(0);
    playerRef.current.pause();
  };

  return { init, cleanup, playerRef, exitFullscreen, pausePlayer, resetPlayer };
};

export default useWebGlPlayer;
